import React, { useState, useEffect } from 'react';
import { Info, DollarSign, Cpu, HardDrive, Zap, TrendingUp } from 'lucide-react';
import { ApiService } from '../../../services/api';
import { ComputePrice, StorageSystem } from '../../../types/api';

const PricingStandard: React.FC = () => {
  const [computeRegion, setComputeRegion] = useState('通用计算区');
  const [computePrices, setComputePrices] = useState<ComputePrice[]>([]);
  const [storageSystems, setStorageSystems] = useState<StorageSystem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPricing = async () => {
      try {
        const pricing = await ApiService.getPricing({ region: computeRegion });
        setComputePrices(pricing.computePrices);
      } catch (error) {
        console.error('Failed to fetch compute pricing:', error);
      }
    };

    const fetchStoragePricing = async () => {
      try {
        const pricing = await ApiService.getPricing({ type: 'storage' });
        setStorageSystems(pricing.storageSystems || []);
      } catch (error) {
        console.error('Failed to fetch storage pricing:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPricing();
    fetchStoragePricing();
  }, [computeRegion]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex justify-center items-center">
        <div className="flex items-center space-x-4">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-600 border-t-transparent"></div>
          <div className="text-xl font-medium text-gray-700">加载定价信息中...</div>
        </div>
      </div>
    );
  }

  const filteredPrices = computeRegion === 'GPU计算区'
    ? computePrices.filter(price => price.gpus !== '—')
    : computePrices.filter(price => price.gpus === '—');

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-6">
      <div className="max-w-7xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-600 via-indigo-600 to-purple-600 rounded-3xl flex items-center justify-center shadow-2xl transform hover:scale-105 transition-transform">
                <DollarSign className="w-10 h-10 text-white" />
              </div>
              <div className="absolute -top-2 -right-2 w-8 h-8 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                <TrendingUp className="w-4 h-4 text-white" />
              </div>
            </div>
          </div>
          <h1 className="text-5xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
            收费标准
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            透明公开的定价体系，按需付费，灵活可控
          </p>
        </div>

        {/* Pricing Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">计算节点</p>
                <p className="text-2xl font-bold text-blue-600">
                  {filteredPrices.length}+
                </p>
                <p className="text-xs text-gray-500 mt-1">规格可选</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Cpu className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">存储方案</p>
                <p className="text-2xl font-bold text-emerald-600">4</p>
                <p className="text-xs text-gray-500 mt-1">类型可选</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <HardDrive className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">最低价格</p>
                <p className="text-2xl font-bold text-purple-600">¥0.5</p>
                <p className="text-xs text-gray-500 mt-1">每小时起</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <Zap className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl p-6 border border-white/20 shadow-xl hover:shadow-2xl transition-all group">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">付费模式</p>
                <p className="text-2xl font-bold text-orange-600">按需</p>
                <p className="text-xs text-gray-500 mt-1">秒级计费</p>
              </div>
              <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-red-500 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
            </div>
          </div>
        </div>

        {/* Compute Resources */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                  <Cpu className="w-5 h-5 text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900">计算资源</h3>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={computeRegion}
                  onChange={(e) => setComputeRegion(e.target.value)}
                  className="bg-white/80 backdrop-blur-sm border border-gray-200 rounded-xl px-4 py-2 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                >
                  <option value="通用计算区">通用计算区</option>
                  <option value="GPU计算区">GPU计算区</option>
                </select>
              </div>
            </div>
          </div>
          
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full table-fixed min-w-[800px] lg:min-w-[1200px]">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th className="w-[150px] lg:w-[200px] px-3 lg:px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">类型</th>
                  <th className="w-[80px] lg:w-[100px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">核数</th>
                  <th className="w-[80px] lg:w-[100px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">卡数</th>
                  <th className="w-[100px] lg:w-[120px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">内存大小</th>
                  <th className="w-[120px] lg:w-[150px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">队列</th>
                  <th className="w-[120px] lg:w-[140px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">标准型单价</th>
                  <th className="w-[120px] lg:w-[140px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">经济型单价</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredPrices.map((price, index) => (
                  <tr key={index} className={`${index % 2 === 0 ? 'bg-white/60' : 'bg-gray-50/60'} hover:bg-blue-50/60 transition-colors`}>
                    <td className="px-3 lg:px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2 lg:space-x-3">
                        <div className="w-8 h-8 lg:w-10 lg:h-10 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-xl flex items-center justify-center">
                          <Cpu className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-900 text-sm lg:text-base">{price.type}</div>
                          <div className="text-xs lg:text-sm text-gray-500">{price.model}</div>
                        </div>
                      </div>
                    </td>
                    <td className="px-3 lg:px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2 lg:px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {price.cores}
                      </span>
                    </td>
                    <td className="px-3 lg:px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2 lg:px-2.5 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        {price.gpus}
                      </span>
                    </td>
                    <td className="px-3 lg:px-6 py-4 text-center">
                      <span className="inline-flex items-center px-2 lg:px-2.5 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-800">
                        {price.memory}
                      </span>
                    </td>
                    <td className="px-3 lg:px-6 py-4 text-center text-xs lg:text-sm text-gray-500 font-medium">{price.queue}</td>
                    <td className="px-3 lg:px-6 py-4 text-center">
                      <div className="font-semibold text-gray-900 text-sm lg:text-base">{price.standardPrice}</div>
                    </td>
                    <td className="px-3 lg:px-6 py-4 text-center">
                      <div className="font-semibold text-emerald-600 text-sm lg:text-base">{price.economyPrice}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          <div className="p-6 bg-gradient-to-r from-yellow-50 to-orange-50 border-t border-yellow-200">
            <div className="flex items-center space-x-2 text-sm text-yellow-800">
              <Info className="w-4 h-4" />
              <span>* 均衡策略单价为默认策略的125%，性能策略单价为默认策略的200%</span>
            </div>
          </div>
        </div>

        {/* Storage Resources */}
        <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl overflow-hidden">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-xl flex items-center justify-center">
                <HardDrive className="w-5 h-5 text-white" />
              </div>
              <h3 className="text-xl font-bold text-gray-900">存储资源</h3>
            </div>
          </div>
          
          <div className="overflow-x-auto scrollbar-hide">
            <table className="w-full table-fixed min-w-[600px] lg:min-w-[800px]">
              <thead>
                <tr className="bg-gradient-to-r from-gray-50 to-gray-100">
                  <th className="w-[150px] lg:w-[200px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">节点存储</th>
                  <th className="w-[150px] lg:w-[200px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                    <div className="flex items-center justify-center space-x-1 lg:space-x-2">
                      <span className="text-xs lg:text-xs">弹性并行文件系统</span>
                      <div className="group relative">
                        <Info className="w-3 h-3 lg:w-4 lg:h-4 text-gray-400 cursor-help" />
                        <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 bg-black text-white text-xs rounded py-1 px-2 opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                          高性能并行文件系统
                        </div>
                      </div>
                    </div>
                  </th>
                  <th className="w-[150px] lg:w-[200px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">集中存储</th>
                  <th className="w-[150px] lg:w-[200px] px-3 lg:px-6 py-4 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">快照存储</th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white/60 hover:bg-blue-50/60 transition-colors">
                  <td className="px-6 py-6 text-center">
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                          <div className="font-semibold text-emerald-800">0GB~80GB</div>
                        </div>
                        <div className="text-lg font-bold text-emerald-600">免费</div>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                          <div className="font-semibold text-blue-800">80GB+</div>
                        </div>
                        <div className="text-lg font-bold text-blue-600">¥2.5/小时/TB</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-6 text-center">
                    <div className="space-y-4">
                      {storageSystems
                        .filter(system => system.name === '弹性并行文件系统')
                        .map((system, index) => (
                          <div key={index} className="p-4 bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl border border-purple-200">
                            <div className="font-semibold text-purple-800 mb-2 text-center">
                              {system.specs.split('\n')[0]}
                            </div>
                            <div className="text-sm text-purple-600 mb-2 text-center">
                              {system.specs.split('\n').slice(1).join('\n')}
                            </div>
                            <div className="text-lg font-bold text-purple-600 text-center">{system.price}</div>
                          </div>
                        ))}
                    </div>
                  </td>
                  <td className="px-6 py-6 text-center">
                    <div className="p-4 bg-gradient-to-r from-orange-50 to-red-50 rounded-xl border border-orange-200">
                      <div className="flex items-center justify-center space-x-2 mb-2">
                        <div className="w-3 h-3 bg-orange-500 rounded-full"></div>
                        <div className="font-semibold text-orange-800">0GB+</div>
                      </div>
                      <div className="text-lg font-bold text-orange-600">¥10/天/TB</div>
                    </div>
                  </td>
                  <td className="px-6 py-6 text-center">
                    <div className="space-y-4">
                      <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                          <div className="font-semibold text-emerald-800">活跃快照</div>
                        </div>
                        <div className="text-lg font-bold text-emerald-600">免费</div>
                      </div>
                      <div className="p-4 bg-gradient-to-r from-yellow-50 to-orange-50 rounded-xl border border-yellow-200">
                        <div className="flex items-center justify-center space-x-2 mb-2">
                          <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                          <div className="font-semibold text-yellow-800">静默快照</div>
                        </div>
                        <div className="text-lg font-bold text-yellow-600">¥0.5/小时/TB</div>
                      </div>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          
          <div className="p-6 bg-gradient-to-r from-blue-50 to-indigo-50 border-t border-blue-200">
            <div className="flex items-center space-x-2 text-sm text-blue-800">
              <Info className="w-4 h-4" />
              <span>* 存储用量以1G为最小单元进行统计</span>
            </div>
          </div>
        </div>

        {/* Pricing Features */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8 text-center group hover:shadow-2xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <Zap className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">按需付费</h4>
            <p className="text-gray-600 leading-relaxed">
              无需预付费用，按实际使用量计费。支持秒级计费，用多少付多少，灵活控制成本。
            </p>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8 text-center group hover:shadow-2xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-teal-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <TrendingUp className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">透明定价</h4>
            <p className="text-gray-600 leading-relaxed">
              所有价格公开透明，无隐藏费用。支持多种规格选择，满足不同场景需求。
            </p>
          </div>
          
          <div className="bg-white/70 backdrop-blur-sm rounded-2xl border border-white/20 shadow-xl p-8 text-center group hover:shadow-2xl transition-all">
            <div className="w-16 h-16 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform">
              <DollarSign className="w-8 h-8 text-white" />
            </div>
            <h4 className="text-xl font-bold text-gray-900 mb-4">成本优化</h4>
            <p className="text-gray-600 leading-relaxed">
              提供标准型和经济型两种计费模式，可根据业务需求选择最优价格策略。
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PricingStandard;
